import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useGetDocumentDetail } from "@/api/client-document";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import {
  DetailCardSkeleton,
  DetailHeaderSkeleton,
} from "@/components/ui/detail-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { requireMinRole } from "@/lib/route-guards";
import { DocumentDetailsPage } from "@/sections/documents/details";
import type { DocumentDetailTab } from "@/sections/documents/details/types";

interface DocumentDetailSearch {
  /** Overview is the canonical view, so it is the absence of this param. */
  tab?: "equipment";
  /** The linked-equipment filter, in the URL for the same reason `q` is on the library. */
  eq?: string;
}

export const Route = createFileRoute("/dashboard/documents/$documentId")({
  component: Component,
  beforeLoad: ({ context }) => {
    requireMinRole(context.session, "viewer");
  },
  staticData: {
    title: "Document details",
    parent: { title: "Documents", path: "/dashboard/documents" },
  },
  validateSearch: (search: Record<string, unknown>): DocumentDetailSearch => ({
    tab: search.tab === "equipment" ? "equipment" : undefined,
    eq: typeof search.eq === "string" && search.eq ? search.eq : undefined,
  }),
});

function DocumentDetailsSkeleton() {
  return (
    <>
      <DetailHeaderSkeleton subtitle trailingPills={1} />
      <div className="flex gap-2 border-b px-4 py-2 lg:px-7">
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-7 w-36" />
      </div>
      <div className="p-4 pb-24 lg:p-7">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <DetailCardSkeleton rows={5} />
            <DetailCardSkeleton rows={6} />
          </div>
          <DetailCardSkeleton rows={3} />
        </div>
      </div>
    </>
  );
}

function Component() {
  const { documentId } = Route.useParams();
  const { tab, eq } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: document, isLoading, error } = useGetDocumentDetail(documentId);

  if (isLoading) {
    return <DocumentDetailsSkeleton />;
  }

  if (error || !document) {
    // document-svc answers 404 for a document that isn't a typed site record as
    // well as one that doesn't exist, so this deliberately doesn't claim which.
    return (
      <PageStatus
        variant="error"
        title="Failed to load document"
        description="We couldn't load this document. It may have been removed, or there was a problem reaching the server. Try again, or return to the document library."
        actions={
          <>
            <Button
              nativeButton={false}
              render={<Link to="/dashboard/documents" />}
              variant="outline"
            >
              Back to documents
            </Button>
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </>
        }
      />
    );
  }

  return (
    <DocumentDetailsPage
      document={document}
      tab={(tab ?? "overview") as DocumentDetailTab}
      onTabChange={(next) =>
        navigate({
          search: (prev) => ({
            ...prev,
            tab: next === "equipment" ? "equipment" : undefined,
          }),
        })
      }
      eq={eq}
      onFilterCommit={(value) =>
        navigate({ search: (prev) => ({ ...prev, eq: value }) })
      }
    />
  );
}
